import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import {
  useForm,
  Controller,
  FormProvider,
  SubmitHandler,
  FieldValues,
} from "react-hook-form";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import LoadingButton from "@mui/lab/LoadingButton";
import { Flag } from "../types";
import { useTranslation } from "../../../../hooks/useTranslation";

type Props = {
  open: boolean;
  onClose: () => void;
  flag?: Flag; //!EDIT IF THE FLAG EXISTS; OTHERWISE, CREATE. UNDERSTAND???
};

type FlagForm = {
  flagName: string;
  flagColor: string;
};

export const CreateEditFlag: React.FC<Props> = ({ open, onClose, flag }) => {
  const { translate, translateWithoutPrefix } =
    useTranslation("pages.orders.flag");

  const methods = useForm<FlagForm>({
    mode: "onSubmit",
    defaultValues: {
      flagName: flag?.flagName ?? "",
      flagColor: flag?.flagColor ?? "#ff0000",
    },
  });

  const { control, handleSubmit, watch, reset } = methods;
  const watchName = watch("flagName");
  const watchColor = watch("flagColor");

  const invalidate = useInvalidate();
  const { mutate, isLoading } = useCustomMutation();

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    mutate(
      {
        url: flag ? `store/user-flags/${flag?.id}` : "store/user-flags",
        method: flag ? "put" : "post",
        values: data,
        successNotification: () => ({
          message: translate(flag ? "successEdit" : "successCreate"),
          type: "success",
        }),
      },
      {
        onSuccess: () => {
          invalidate({
            resource: "store/user-flags",
            invalidates: ["list", "all"],
          });
          reset();
          onClose();
        },
      },
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <FormProvider {...methods}>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{translate(flag ? "editFlag" : "createFlag")}</DialogTitle>

        <DialogContent>
          <Stack direction="row" alignItems="center" gap={2}>
            <Controller
              name="flagName"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={translate("flagTitle")}
                  fullWidth
                  sx={{ mt: 1 }}
                />
              )}
            />

            <Controller
              name="flagColor"
              control={control}
              render={({ field }) => (
                <Box position="relative" sx={{ width: 42, height: 42 }}>
                  <FlagIcon
                    sx={{
                      color: field.value,
                      fontSize: 42,
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      cursor: "pointer",
                    }}
                  />
                  <input
                    type="color"
                    {...field}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                </Box>
              )}
            />
          </Stack>

          <Stack alignItems="center" justifyContent="center" mt={2} spacing={1}>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="h6">{watchName}</Typography>
              {watchName && (
                <FlagIcon fontSize="medium" sx={{ color: watchColor }} />
              )}
            </Stack>
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="body1">{watchName}</Typography>
              {watchName && (
                <FlagIcon fontSize="small" sx={{ color: watchColor }} />
              )}
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            {translateWithoutPrefix("buttons.cancel")}
          </Button>
          <LoadingButton
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary"
            loading={isLoading}
            disabled={!watchName.trim()}
          >
            {translateWithoutPrefix(flag ? "buttons.save" : "buttons.create")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};
